<template>
  <CarouselSwipe v-model="reviewIndex" width="full" :order="['symbols', 'feeling', 'events', 'only', 'recommend', 'comment', 'rating']" class="text-center" :spacing="12" pagination="none">
    <template #symbols>
      <div class="space-y-3 h-full flex flex-col justify-center">
        <ImageEmoji name="LGBTQIA" class="size-6 mx-auto !-mb-1" size="sm" />
        <p class="font-medium !leading-tight">{{ $t('components.Place.review.form.step0.title') }}</p>

        <ButtonToggleLabels
          v-model="review.symbols"
          @change="onSelect"
          is-separations
          class="!bg-light-300 dark:!bg-dark-950"
          :button="{
            size: 'xs'
          }"
          :options="[
            { label: $t('common.no'), value: 'no' },
            { label: $t('common.yes'), value: 'yes' },
          ]"
        />
      </div>
    </template>

    <template #feeling>
      <div class="space-y-3 h-full flex flex-col justify-center">
        <ImageEmoji name="Relieved+Face" class="size-6 mx-auto !-mb-1" size="sm" />
        <p class="font-medium !leading-tight">{{ $t('components.Place.review.form.step1.title') }}</p>

        <ButtonToggleLabels
          v-model="review.feeling"
          @change="onSelect"
          is-separations
          class="!bg-light-300 dark:!bg-dark-950"
          :button="{
            size: 'xs'
          }"
          :options="[
            { label: $t('common.no'), value: 'no' },
            { label: $t('common.yes'), value: 'yes' },
          ]"
        />
      </div>
    </template>

    <template #events>
      <div class="space-y-3 h-full flex flex-col justify-center">
        <ImageEmoji name="Spiral+Calendar" class="size-6 mx-auto !-mb-1" size="sm" />
        <p class="font-medium !leading-tight">{{ $t('components.Place.review.form.step2.title') }}</p>
        
        <ButtonToggleLabels
          v-model="review.events"
          @change="onSelect"
          is-separations
          class="!bg-light-300 dark:!bg-dark-950"
          :button="{
            size: 'xs'
          }"
          :options="[
            { label: $t('common.no'), value: 'no' },
            { label: $t('common.dontKnow'), value: 'maybe' },
            { label: $t('common.yes'), value: 'yes' },
          ]"
        />
      </div>
    </template>

    <template #only>
      <div class="space-y-3 h-full flex flex-col justify-center">
        <ImageEmoji name="Waving+Hand" class="size-6 mx-auto !-mb-1" size="sm" />

        <div class="space-y-1">
          <p class="font-medium !leading-tight">{{ $t('components.Place.review.form.step5.title') }}</p>
          <p class="!leading-tight text-xs font-normal text-light-800 dark:text-dark-400">{{ $t('components.Place.review.form.step5.content') }}</p>
        </div>
        
        <ButtonToggleLabels
          v-model="review.only"
          @change="onSelect"
          is-separations
          class="!bg-light-300 dark:!bg-dark-950"
          :button="{
            size: 'xs'
          }"
          :options="[
            { label: $t('common.no'), value: 'no' },
            { label: $t('common.dontKnow'), value: 'maybe' },
            { label: $t('common.yes'), value: 'yes' },
          ]"
        />
      </div>
    </template>

    <template #comment>
      <div class="space-y-2 h-full flex flex-col justify-center">
        <InputTextarea
          class="p-3 rounded-xl border border-charcoal-100 dark:bg-dark-800 dark:border-dark-600"
          v-model="review.comment"
          :max-height="75"
          :placeholder="`${title ? title : $t('components.Place.review.form.step3.placeholder')}`"
        />
        <ButtonBase color="white" size="xs" is-vibrate @click="onSelect">
          {{ review.comment.length > 0 ? $t('actions.continue') : $t('actions.skip') }}
        </ButtonBase>
      </div>
    </template>

    <template #rating>
      <div class="h-full flex flex-col justify-center">
        <p class="font-medium mb-1 !leading-tight">
          {{ $t('components.Place.review.form.step4.title') }}
        </p>

        <InputRating v-model="review.rating" />          

        <div class="flex items-center mt-4 space-x-1.5">
          <InputCheckbox id="anonymous" v-model="review.isAnonymous" class="bg-whale-100 text-whale-950 dark:bg-whale-950 dark:text-whale-300 py-2.5 px-3 rounded-full">
            <p class="text-sm font-medium leading-tight pt-0.5 -ml-1">
              {{ $t('components.Place.review.form.step4.anonymous') }}
            </p>
          </InputCheckbox>

          <ButtonBase size="xs" is-full :is-disabled="review.rating === 0" :is-loading="isLoadingReview" is-vibrate @click="onSubmitReview">
            {{ $t('actions.submit') }}
          </ButtonBase>
        </div>
      </div>
    </template>
  </CarouselSwipe>
</template>

<script lang="ts" setup>
import PlacesStore from '@/store/places'
import { Place } from '@/classes'
import UserStore from '@/store/user'
import ImageEmoji from '../Image/ImageEmoji.vue'

const props = withDefaults(defineProps<{
  place: Place
  title?: string
}>(), {
  title: ''
})

const emit = defineEmits<{
  (e: 'end'): void
}>()

const { place } = toRefs(props)

const placesStore = PlacesStore()
const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const onSelect = () => {
  setTimeout(() => {
    reviewIndex.value += 1
  }, 100)
}

// REVIEW

const reviewIndex = ref(0)
const isLoadingReview = ref(false)
const isReview = ref(false)

const review: {
  comment: string
  recommend: 'yes' | 'no' | 'maybe' | ''
  events: 'yes' | 'no' | 'maybe' | ''
  only: 'yes' | 'no' | 'maybe' | ''
  feeling: 'yes' | 'no' | ''
  symbols: 'yes' | 'no' | ''
  rating: number
  isAnonymous: boolean
} = reactive({
  comment: '',
  recommend: '',
  events: '',
  only: '',
  feeling: '',
  symbols: '',
  rating: 0,
  isAnonymous: false
})

const recommendedRating = computed(() => {
  let rating = 1

  if (review.symbols === 'yes') rating += 1
  
  if (review.feeling === 'yes') rating += 2
  if (review.feeling === 'no') rating -= 1.5

  if (review.events === 'yes') rating += 0.5
  if (review.only === 'yes') rating += 1
  
  return Math.floor(Math.max(1, Math.min(5, rating)))
})


const onSubmitReview = async () => {
  isLoadingReview.value = true

  const response = await placesStore.addReview(place.value?._id, review)
  if (response.ok) {
    emit('end')
    isReview.value = false
  }

  isLoadingReview.value = false
}

const foundReview = computed(() => (place.value?.reviewsVisible ?? []).find(r => r.owner === currentUser.value?._id))

watch(recommendedRating, () => {
  if (!foundReview.value) {
    review.rating = recommendedRating.value
  }
})

watch(foundReview, () => {
  if (foundReview.value) {
    review.comment = foundReview.value.comment
    review.events = foundReview.value.events
    review.feeling = foundReview.value.feeling
    review.only = foundReview.value.only
    review.symbols = foundReview.value.symbols
    review.rating = foundReview.value.rating
    review.isAnonymous = foundReview.value.isAnonymous
  }
}, { immediate: true })

</script>